import React, { FunctionComponent, useState } from "react";
import styles from "./styles.module.scss";
import {
  Box,
  DialogActions,
  DialogContent,
  ToggleButton,
  ToggleButtonGroup,
  Toolbar,
  useTheme
} from "@mui/material";
import { IAlbumType } from "../../../../hooks/albumTypes";
import { strings } from "../../../../localization/LocalizedStrings";
import { TextField } from "../../../TextField";
import { FormikProvider, useFormik } from "formik";
import * as Yup from "yup";
import { useAddAlbumType } from "../../../../hooks/albumTypes/useAddAlbumType";
import { useModifyAlbumType } from "../../../../hooks/albumTypes/useModifyAlbumType";
import { useDeleteAlbumType } from "../../../../hooks/albumTypes/useDeleteAlbumType";
import { AlertDialog } from "../../../AlertDialog";
import { StatusCodesHelper } from "../../../../models/StatusCodesHelper";
import { FieldTitle } from "../../../Text/FieldTitle";
import { ContainedButton } from "../../../ContainedButton";
import { ToolbarButton } from "../../../toolbar/ToolbarButton";
import { useIsMobile } from "../../../../hooks/isMobile";
import { Firebase } from "../../../../services/Firebase";
import { ProductPurchasesDialog } from "../../../products/ProductPurchasesDialog";
import { ProductType } from "../../../../hooks/products/interfaces";
import { PiperDialog } from "../../../PiperDialog";
import { IconType } from "../../../../icons";

export const ModifyAlbumTypeDialog: FunctionComponent<IModifyAlbumTypeDialogProps> = ({
  open,
  onClose,
  albumType,
  onNewItem,
  onModifiedItem,
  onDeletedItem
}) => {
  const theme = useTheme();
  const isMobile = useIsMobile();
  const dialogTitle = albumType ? strings.album_type_title : strings.mat_new_title;
  const { addAlbumType } = useAddAlbumType();
  const { modifyAlbumType } = useModifyAlbumType();
  const { deleteAlbumType } = useDeleteAlbumType();
  const [isLoading, setIsLoading] = useState(false);
  const [alertMessage, setAlertMessage] = useState("");
  const [alertIsOpen, setAlertIsOpen] = useState(false);
  const [isPurchaseAlertOpen, setIsPurchaseAlertOpen] = useState(false);
  const [isPurchaseDialogOpen, setIsPurchaseDialogOpen] = useState(false);
  const [isConfirmCloseAlertOpen, setIsConfirmCloseAlertOpen] = useState(false);
  const tryToClose = () => {
    if (isLoading) return;
    if (formik.dirty) {
      setIsConfirmCloseAlertOpen(true);
    } else {
      onClose();
    }
  };
  const validationSchema = Yup.object({
    name: Yup.string().required(strings.mat_missing_error)
  });
  const formik = useFormik({
    initialValues: {
      name: albumType?.name ?? "",
      showOnAlbumList: albumType?.showOnAlbumList ?? true
    },
    enableReinitialize: true,
    validateOnBlur: false,
    validateOnChange: false,
    onSubmit: async ({ name, showOnAlbumList }) => {
      setIsLoading(true);
      if (albumType) {
        const { status, body } = await modifyAlbumType({
          uuid: albumType.uuid,
          name,
          showOnAlbumList
        });
        if (StatusCodesHelper.isSuccessful(status)) {
          Firebase.shared.logFirebaseEvent("attribute_albumtype_action", { isNew: false });
          onModifiedItem?.(body);
          onClose();
        } else {
          setAlertMessage(body.message);
          setAlertIsOpen(true);
        }
      } else {
        const { status, body } = await addAlbumType({ name, showOnAlbumList });
        if (StatusCodesHelper.isSuccessful(status)) {
          Firebase.shared.logFirebaseEvent("attribute_albumtype_action", { isNew: true });
          onNewItem?.(body);
          onClose();
        } else if (StatusCodesHelper.isLimitReached(status)) {
          setAlertMessage(body.message);
          setIsPurchaseAlertOpen(true);
        } else {
          setAlertMessage(body.message);
          setAlertIsOpen(true);
        }
      }
      setIsLoading(false);
    },
    validationSchema: validationSchema
  });
  const onDelete = async () => {
    if (!albumType) return;
    setIsLoading(true);
    const { status, body } = await deleteAlbumType({ uuid: albumType.uuid });
    if (StatusCodesHelper.isSuccessful(status)) {
      onDeletedItem?.(albumType);
      onClose();
    } else {
      setAlertMessage(body.message);
      setAlertIsOpen(true);
    }
    setIsLoading(false);
  };
  return (
    <FormikProvider value={formik}>
      <PiperDialog fullWidth maxWidth={"xs"} open={open} onClose={tryToClose}>
        <Toolbar>
          <h3>{dialogTitle}</h3>
          <Box sx={{ flexGrow: 1 }} />
          {albumType && (
            <ToolbarButton
              onClick={onDelete}
              tooltip={strings.delete}
              iconType={IconType.DeleteForever}
              color={theme.palette.colors?.remove}
            />
          )}
          <ToolbarButton
            loading={isLoading}
            onClick={tryToClose}
            tooltip={strings.close}
            iconType={IconType.Close}
          />
        </Toolbar>
        <DialogContent sx={{ paddingTop: 0, paddingBottom: 0 }}>
          <FieldTitle>{strings.mat_field_title}</FieldTitle>
          <div className={styles.textField}>
            <TextField
              id="name"
              name="name"
              size="small"
              autoFocus={!isMobile}
              placeholder={strings.mat_placeholder}
              value={formik.values.name}
              onChange={formik.handleChange}
            />
          </div>
          <FieldTitle>{strings.mat_show_on_album_list_title}</FieldTitle>
          <ToggleButtonGroup
            value={formik.values.showOnAlbumList}
            exclusive
            onChange={(_event, value) => {
              if (value !== null) {
                formik.setFieldValue("showOnAlbumList", value).then();
              }
            }}
            sx={{ height: "30px", width: "100%" }}
          >
            <ToggleButton
              className={styles.toggleButton}
              sx={{ color: theme.palette.colors?.disabled }}
              value={true}
            >
              {strings.stats_pie_yes}
            </ToggleButton>
            <ToggleButton
              className={styles.toggleButton}
              sx={{ color: theme.palette.colors?.disabled }}
              value={false}
            >
              {strings.stats_pie_no}
            </ToggleButton>
          </ToggleButtonGroup>
        </DialogContent>
        <DialogActions>
          <ContainedButton type={"submit"} disabled={isLoading} onClick={formik.handleSubmit}>
            {strings.alert_confirm}
          </ContainedButton>
        </DialogActions>
      </PiperDialog>
      <AlertDialog
        message={strings.alert_cancel_confirmation}
        open={isConfirmCloseAlertOpen}
        setOpen={setIsConfirmCloseAlertOpen}
        isConfirm={true}
        onConfirm={onClose}
        confirmTitle={strings.yes}
        cancelTitle={strings.no}
      />
      <AlertDialog message={alertMessage} open={alertIsOpen} setOpen={setAlertIsOpen} />
      <AlertDialog
        message={alertMessage}
        open={isPurchaseAlertOpen}
        setOpen={setIsPurchaseAlertOpen}
        isConfirm
        confirmTitle={strings.purchase_alert}
        onConfirm={() => setIsPurchaseDialogOpen(true)}
      />
      <ProductPurchasesDialog
        productType={ProductType.ALBUM_TYPES}
        open={isPurchaseDialogOpen}
        setOpen={setIsPurchaseDialogOpen}
      />
    </FormikProvider>
  );
};

export interface IModifyAlbumTypeDialogProps {
  open: boolean;
  onClose: () => void;
  albumType?: IAlbumType;
  onNewItem?: (item: IAlbumType) => void;
  onModifiedItem?: (item: IAlbumType) => void;
  onDeletedItem?: (item: IAlbumType) => void;
}
